import moment, { Moment } from 'moment';
import { Control, Controller, FieldValues, Path } from 'react-hook-form';
import { ISize } from '../interface';
import { StyledDatePicker } from '../FormDatePicker/FormDatePicker.styles';
import { Grid, Typography } from '../../base-components';
import { formatToYYYYMMDD } from '../../../common/utils/date.utils';

interface IDateRangeValue {
    from: string | null;
    to: string | null;
}

interface Props<F extends FieldValues> {
    name: Path<F>;
    control: Control<F>;
    fromLabel?: string;
    toLabel?: string;
    disabled?: boolean;
    size?: ISize;
}

const FormDateRangePicker = <F extends FieldValues>(props: Props<F>) => {
    const {
        name,
        control,
        fromLabel,
        toLabel,
        disabled,
        size = 'small',
    } = props;

    return (
        <Controller
            name={name}
            control={control}
            render={({ field: { onChange, value }, fieldState: { error } }) => {
                const dateValue = value as IDateRangeValue | null;

                const fromMoment =
                    dateValue?.from && typeof dateValue.from === 'string'
                        ? moment(dateValue.from)
                        : null;

                const toMoment =
                    dateValue?.to && typeof dateValue.to === 'string'
                        ? moment(dateValue.to)
                        : null;

                const handleFromChange = (date: Moment | null) => {
                    onChange({
                        from: date ? formatToYYYYMMDD(date.toISOString()) : null,
                        to: dateValue?.to || null,
                    });
                };

                const handleToChange = (date: Moment | null) => {
                    onChange({
                        from: dateValue?.from || null,
                        to: date ? formatToYYYYMMDD(date.toISOString()) : null,
                    });
                };

                return (
                    <>
                        <Grid container spacing={2}>
                            <Grid item xs={6}>
                                <StyledDatePicker
                                    label={fromLabel}
                                    value={fromMoment}
                                    onChange={handleFromChange}
                                    maxDate={toMoment || undefined}
                                    slotProps={{
                                        textField: {
                                            variant: 'outlined',
                                            size: size,
                                            error: Boolean(error),
                                            sx: {
                                                '& .MuiInputBase-root': {
                                                    borderRadius: '15px',
                                                },
                                            },
                                        },
                                    }}
                                    format="DD/MM/YYYY"
                                    disabled={disabled}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <StyledDatePicker
                                    label={toLabel}
                                    value={toMoment}
                                    onChange={handleToChange}
                                    minDate={fromMoment || undefined}
                                    slotProps={{
                                        textField: {
                                            variant: 'outlined',
                                            size: size,
                                            error: Boolean(error),
                                            sx: {
                                                '& .MuiInputBase-root': {
                                                    borderRadius: '15px',
                                                },
                                            },
                                        },
                                    }}
                                    format="DD/MM/YYYY"
                                    disabled={disabled}
                                />
                            </Grid>
                        </Grid>
                        {error?.message && (
                            <Typography
                                variant="caption"
                                color="error"
                                sx={{ mt: 0.5, display: 'block' }}
                            >
                                {error.message}
                            </Typography>
                        )}
                    </>
                );
            }}
        />
    );
};

export default FormDateRangePicker;
